import clsx from 'clsx'
import renderAmountField from 'components/FormControls/amountField'
import renderDatepickerField from 'components/FormControls/datepickerField'
import renderMaskedInput from 'components/FormControls/maskedInputField'
import renderSelect from 'components/FormControls/select'
import renderInputField from 'components/FormControls/textInput'
import { format, parse } from 'date-fns'
import Decimal from 'decimal.js'
import { Icon, Tooltip, Typography } from 'gu-ui-kit'
import formStyles from 'modules/Forms/common-form.module.scss'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { autofill, Field } from 'redux-form'
import phoneMask from 'utils/constants/phoneMask'
import phoneFieldNormalize from 'utils/normalize/phoneFieldNormalize'
import renderCheckbox from 'components/FormControls/checkboxField'

const AdvertiserFieldsExtended = ({
  builtInto = false,
  form,
  advertiser_info,
  showContractTypeOtherOption = false,
  networkType = '',
  actionButtons = null,
}) => {
  const { t } = useTranslation([])
  const { i18n } = useSelector(({ app }) => app)
  const dispatch = useDispatch()

  const { isYandexBusiness, isYd, isVk } = useMemo(
    () => ({
      isYandexBusiness: networkType === 'yandex-business',
      isYd: networkType === 'yd',
      isVk: networkType === 'vk',
    }),
    [networkType],
  )

  useEffect(() => {
    if (isYandexBusiness) {
      dispatch(
        autofill(
          form,
          'advertiser_info.contract.is_charge_paid_by_agent',
          advertiser_info?.contract?.type === 'intermediary_contract' ? false : undefined,
        ),
      )
    }
  }, [advertiser_info?.contract?.type, isYandexBusiness])

  return (
    <>
      <div className={'margin-bottom-16'}>
        <Typography
          component={'h3'}
          variant={builtInto ? 'Subtitle-20' : 'Subtitle-30'}
          className={'margin-bottom-16'}
        >
          {t('services::forms.shared_fields.advertiser.texts.advertiser_information_header')}
        </Typography>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            label={t('services::forms.shared_fields.advertiser.fields.advertiser_info.type.label')}
            name="advertiser_info.type"
            component={renderSelect}
            fullWidth
            t={t}
            emptyOptionLabel={t(
              'services::forms.shared_fields.advertiser.fields.advertiser_info.type.options.empty',
            )}
            clearOptionLabel={t('common::select_clear_option')}
            options={[
              {
                value: 'physical',
                label: t(
                  'services::forms.shared_fields.advertiser.fields.advertiser_info.type.options.physical',
                ),
              },
              {
                value: 'legal',
                label: t(
                  'services::forms.shared_fields.advertiser.fields.advertiser_info.type.options.legal',
                ),
              },
              {
                value: 'individual',
                label: t(
                  'services::forms.shared_fields.advertiser.fields.advertiser_info.type.options.individual',
                ),
              },
            ]}
            normalize={(value = '') => value}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t(
                'services::forms.shared_fields.advertiser.fields.advertiser_info.type.tooltip',
              )}
              placement="top"
              arrow
            >
              <Icon
                name={'information'}
                color={'Label-Secondary'}
                size={'m'}
              />
            </Tooltip>
          </div>
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            fullWidth
            name="advertiser_info.tax_number"
            component={renderInputField}
            label={t(
              'services::forms.shared_fields.advertiser.fields.advertiser_info.tax_number.label',
            )}
            normalize={(value) => value.replace(/\D/g, '').substr(0, 12) ?? ''}
            t={t}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t(
                'services::forms.shared_fields.advertiser.fields.advertiser_info.tax_number.tooltip',
              )}
              placement="top"
              arrow
            >
              <Icon
                name={'information'}
                color={'Label-Secondary'}
                size={'m'}
              />
            </Tooltip>
          </div>
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            fullWidth
            name="advertiser_info.name"
            component={renderInputField}
            label={t(`services::forms.shared_fields.advertiser.fields.advertiser_info.name.label`, {
              context: advertiser_info?.type === 'legal' ? 'legal' : '',
            })}
            t={t}
            normalize={(value) => value.replace(/[«|»]/g, '"')}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t(
                'services::forms.shared_fields.advertiser.fields.advertiser_info.name.tooltip',
              )}
              placement="top"
              arrow
            >
              <Icon
                name={'information'}
                color={'Label-Secondary'}
                size={'m'}
              />
            </Tooltip>
          </div>
        </div>

        {isYd && (
          <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
            <Field
              fullWidth
              name="advertiser_info.okved_code"
              component={renderInputField}
              label={t(
                `services::forms.shared_fields.advertiser.fields.advertiser_info.okved_code.label`,
              )}
              t={t}
              maxLength={8}
              normalize={(value = '') => {
                let matched = null
                if (value) {
                  matched = value.replace(/^(\.+)/g, '')
                  matched = matched.replace(/\.{2,}/g, '.')
                  matched = matched.match(/[.\d]+/)?.[0]
                }
                return matched
              }}
            />
            <div className={formStyles['tooltip-container']}>
              <Tooltip
                title={t(
                  'services::forms.shared_fields.advertiser.fields.advertiser_info.okved_code.tooltip',
                )}
                placement="top"
                arrow
              >
                <Icon
                  name={'information'}
                  color={'Label-Secondary'}
                  size={'m'}
                />
              </Tooltip>
            </div>
          </div>
        )}

        {!isYandexBusiness && (
          <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
            <Field
              fullWidth
              normalize={phoneFieldNormalize}
              name="advertiser_info.phone"
              component={renderMaskedInput}
              placeholderChar={'\u2002'}
              placeholder={'+ ( )'}
              mask={phoneMask}
              label={t(
                'services::forms.shared_fields.advertiser.fields.advertiser_info.phone.label',
              )}
              t={t}
            />
            <div className={formStyles['tooltip-container']}>
              <Tooltip
                title={t(
                  'services::forms.shared_fields.advertiser.fields.advertiser_info.phone.tooltip',
                )}
                placement="top"
                arrow
              >
                <Icon
                  name={'information'}
                  color={'Label-Secondary'}
                  size={'m'}
                />
              </Tooltip>
            </div>
          </div>
        )}

        {isYandexBusiness && advertiser_info?.type === 'legal' && (
          <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
            <Field
              fullWidth
              name="advertiser_info.kpp"
              required
              component={renderInputField}
              label={t('services::forms.shared_fields.advertiser.fields.advertiser_info.kpp.label')}
              normalize={(value = '') => value.replace(/\D/g, '').substr(0, 9)}
              t={t}
            />
            <div className={formStyles['tooltip-container']}>
              <Tooltip
                title={t(
                  'services::forms.shared_fields.advertiser.fields.advertiser_info.kpp.tooltip',
                )}
                placement="top"
                arrow
              >
                <Icon
                  name={'information'}
                  color={'secondary-grey'}
                  size={'m'}
                />
              </Tooltip>
            </div>
          </div>
        )}
      </div>

      <div className={'margin-bottom-24'}>
        <Typography
          variant={builtInto ? 'Subtitle-20' : 'Subtitle-30'}
          component={'h3'}
          className={'margin-bottom-16'}
        >
          {t(
            'services::forms.shared_fields.advertiser.texts.advertiser_counterparty_information_header',
          )}
        </Typography>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            label={t(
              'services::forms.shared_fields.advertiser.fields.advertiser_info.counterparty.type.label',
            )}
            name="advertiser_info.counterparty.type"
            component={renderSelect}
            fullWidth
            t={t}
            normalize={(value = '') => value}
            emptyOptionLabel={t(
              'services::forms.shared_fields.advertiser.fields.advertiser_info.type.options.empty',
            )}
            clearOptionLabel={t('common::select_clear_option')}
            options={[
              {
                value: 'legal',
                label: t(
                  'services::forms.shared_fields.advertiser.fields.advertiser_info.type.options.legal',
                ),
              },
              {
                value: 'individual',
                label: t(
                  'services::forms.shared_fields.advertiser.fields.advertiser_info.type.options.individual',
                ),
              },
              {
                value: 'physical',
                label: t(
                  'services::forms.shared_fields.advertiser.fields.advertiser_info.type.options.physical',
                ),
              },
            ]}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t(
                'services::forms.shared_fields.advertiser.fields.advertiser_info.counterparty.type.tooltip',
              )}
              placement="top"
              arrow
            >
              <Icon
                name={'information'}
                color={'Label-Secondary'}
                size={'m'}
              />
            </Tooltip>
          </div>
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            fullWidth
            name="advertiser_info.counterparty.tax_number"
            component={renderInputField}
            label={t(
              'services::forms.shared_fields.advertiser.fields.advertiser_info.counterparty.tax_number.label',
            )}
            normalize={(value) => value.replace(/\D/g, '').substr(0, 12) ?? ''}
            t={t}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t(
                'services::forms.shared_fields.advertiser.fields.advertiser_info.counterparty.tax_number.tooltip',
              )}
              placement="top"
              arrow
            >
              <Icon
                name={'information'}
                color={'Label-Secondary'}
                size={'m'}
              />
            </Tooltip>
          </div>
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            fullWidth
            name="advertiser_info.counterparty.name"
            component={renderInputField}
            label={t(
              'services::forms.shared_fields.advertiser.fields.advertiser_info.counterparty.name.label',
            )}
            t={t}
            normalize={(value) => value.replace(/[«|»]/g, '"')}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t(
                'services::forms.shared_fields.advertiser.fields.advertiser_info.counterparty.name.tooltip',
              )}
              placement="top"
              arrow
            >
              <Icon
                name={'information'}
                color={'Label-Secondary'}
                size={'m'}
              />
            </Tooltip>
          </div>
        </div>

        {!isYandexBusiness && (
          <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
            <Field
              fullWidth
              normalize={phoneFieldNormalize}
              name="advertiser_info.counterparty.phone"
              component={renderMaskedInput}
              label={t(
                'services::forms.shared_fields.advertiser.fields.advertiser_info.counterparty.phone.label',
              )}
              t={t}
              placeholderChar={'\u2002'}
              placeholder={'+ ( )'}
              mask={phoneMask}
            />
            <div className={formStyles['tooltip-container']}>
              <Tooltip
                title={t(
                  'services::forms.shared_fields.advertiser.fields.advertiser_info.counterparty.phone.tooltip',
                )}
                placement="top"
                arrow
              >
                <Icon
                  name={'information'}
                  color={'Label-Secondary'}
                  size={'m'}
                />
              </Tooltip>
            </div>
          </div>
        )}

        {isYandexBusiness && advertiser_info?.counterparty?.type === 'legal' && (
          <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
            <Field
              fullWidth
              name="advertiser_info.counterparty.kpp"
              required
              component={renderInputField}
              label={t('services::forms.shared_fields.advertiser.fields.advertiser_info.kpp.label')}
              normalize={(value = '') => value.replace(/\D/g, '').substr(0, 9)}
              t={t}
            />
            <div className={formStyles['tooltip-container']}>
              <Tooltip
                title={t(
                  'services::forms.shared_fields.advertiser.fields.advertiser_info.kpp.tooltip',
                )}
                placement="top"
                arrow
              >
                <Icon
                  name={'information'}
                  color={'secondary-grey'}
                  size={'m'}
                />
              </Tooltip>
            </div>
          </div>
        )}
      </div>
      <div className={'margin-bottom-24'}>
        <Typography
          variant={builtInto ? 'Subtitle-20' : 'Subtitle-30'}
          component={'h3'}
          className={'margin-bottom-16'}
        >
          {t(
            'services::forms.shared_fields.advertiser.texts.advertiser_contract_information_header',
          )}
        </Typography>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            label={t(
              'services::forms.shared_fields.advertiser.fields.advertiser_info.contract.type.label',
            )}
            name="advertiser_info.contract.type"
            component={renderSelect}
            normalize={(value = '') => value}
            fullWidth
            t={t}
            options={[
              {
                value: 'contract',
                label: t(
                  'services::forms.shared_fields.advertiser.fields.advertiser_info.contract.type.options.contract',
                ),
              },
              {
                value: 'intermediary_contract',
                label: t(
                  'services::forms.shared_fields.advertiser.fields.advertiser_info.contract.type.options.intermediary_contract',
                ),
              },
              {
                value: 'additional_agreement',
                label: t(
                  'services::forms.shared_fields.advertiser.fields.advertiser_info.contract.type.options.additional_agreement',
                ),
              },
            ].concat(
              showContractTypeOtherOption
                ? [
                    {
                      value: 'other',
                      label: t(
                        'services::forms.shared_fields.advertiser.fields.advertiser_info.contract.type.options.other',
                      ),
                    },
                  ]
                : [],
            )}
            emptyOptionLabel={t(
              'services::forms.shared_fields.advertiser.fields.advertiser_info.contract.type.options.empty',
            )}
            clearOptionLabel={t('common::select_clear_option')}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t(
                'services::forms.shared_fields.advertiser.fields.advertiser_info.contract.type.tooltip',
              )}
              placement="top"
              arrow
            >
              <Icon
                name={'information'}
                color={'Label-Secondary'}
                size={'m'}
              />
            </Tooltip>
          </div>
        </div>

        {isYandexBusiness && advertiser_info?.contract?.type === 'intermediary_contract' && (
          <div className={clsx('margin-bottom-16')}>
            <Field
              name="advertiser_info.contract.is_charge_paid_by_agent"
              checked={advertiser_info.contract.is_charge_paid_by_agent}
              component={renderCheckbox}
              label={
                <span
                  dangerouslySetInnerHTML={{
                    __html: t(
                      'services::forms.shared_fields.advertiser.fields.advertiser_info.contract.is_charge_paid_by_agent.label',
                    ),
                  }}
                />
              }
              t={t}
            />
          </div>
        )}

        {isVk && advertiser_info?.contract?.type === 'other' && (
          <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
            <Field
              fullWidth
              name="advertiser_info.contract.type_description"
              component={renderInputField}
              label={t(
                'services::forms.shared_fields.advertiser.fields.advertiser_info.contract.type_description.label',
              )}
              helperText={t(
                'services::forms.shared_fields.advertiser.fields.advertiser_info.contract.type_description.helperText',
                {
                  length: advertiser_info?.contract.type_description?.length || 0,
                },
              )}
              t={t}
              maxLength={255}
            />
          </div>
        )}

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            fullWidth
            name="advertiser_info.contract.number"
            component={renderInputField}
            label={t(
              'services::forms.shared_fields.advertiser.fields.advertiser_info.contract.number.label',
            )}
            normalize={(value) => (value.trim().length > 0 ? value.trim() : null)}
            t={t}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t(
                'services::forms.shared_fields.advertiser.fields.advertiser_info.contract.number.tooltip',
              )}
              placement="top"
              arrow
            >
              <Icon
                name={'information'}
                color={'Label-Secondary'}
                size={'m'}
              />
            </Tooltip>
          </div>
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            fullWidth
            name="advertiser_info.contract.date"
            component={renderDatepickerField}
            mode={'calendar'}
            placeholder={t(
              'services::forms.shared_fields.advertiser.fields.advertiser_info.contract.date.label',
            )}
            minDate={new Date(2000, 0, 1)}
            maxDate={new Date()}
            i18n={i18n}
            t={t}
            normalize={(newValue) => format(newValue, 'yyyy-MM-dd')}
            parseDate={(value) => (!value ? null : parse(value, 'yyyy-MM-dd', new Date()))}
            label={t(
              'services::forms.shared_fields.advertiser.fields.advertiser_info.contract.date.label',
            )}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t(
                'services::forms.shared_fields.advertiser.fields.advertiser_info.contract.date.tooltip',
              )}
              placement="top"
              arrow
            >
              <Icon
                name={'information'}
                color={'Label-Secondary'}
                size={'m'}
              />
            </Tooltip>
          </div>
        </div>

        <div className={'margin-bottom-16'}>
          <div className={formStyles['row_with-tooltip']}>
            <Field
              label={t(
                'services::forms.shared_fields.advertiser.fields.advertiser_info.contract.amount.label',
              )}
              fullWidth
              name="advertiser_info.contract.amount"
              type="text"
              component={renderAmountField}
              t={t}
              isAllowed={({ value }) => {
                if (!value) {
                  return true
                } else {
                  const floatVal = new Decimal(value)
                  return floatVal.lessThanOrEqualTo('1000000000.00')
                }
              }}
              autocomplete="off"
              format={(value) => (value === null || value === undefined ? null : '' + value)}
              parse={(value) => value.replace(/\s/g, '').replace(',', '.')}
              normalize={(value) => (value ? value : null)}
            />
            <div className={formStyles['tooltip-container']}>
              <Tooltip
                title={t(
                  'services::forms.shared_fields.advertiser.fields.advertiser_info.contract.amount.tooltip',
                )}
                placement="top"
                arrow
              >
                <Icon
                  name={'information'}
                  color={'Label-Secondary'}
                  size={'m'}
                />
              </Tooltip>
            </div>
          </div>
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            label={t(
              'services::forms.shared_fields.advertiser.fields.advertiser_info.contract.subject_type.label',
            )}
            name="advertiser_info.contract.subject_type"
            component={renderSelect}
            normalize={(value = '') => value}
            fullWidth
            t={t}
            emptyOptionLabel={t(
              'services::forms.shared_fields.advertiser.fields.advertiser_info.contract.subject_type.options.empty',
            )}
            clearOptionLabel={t('common::select_clear_option')}
            options={[
              {
                value: 'representation',
                label: t(
                  'services::forms.shared_fields.advertiser.fields.advertiser_info.contract.subject_type.options.representation',
                ),
              },
              {
                value: 'mediation',
                label: t(
                  'services::forms.shared_fields.advertiser.fields.advertiser_info.contract.subject_type.options.mediation',
                ),
              },
              {
                value: 'distribution',
                label: t(
                  'services::forms.shared_fields.advertiser.fields.advertiser_info.contract.subject_type.options.distribution',
                ),
              },
              {
                value: 'org_distribution',
                label: t(
                  'services::forms.shared_fields.advertiser.fields.advertiser_info.contract.subject_type.options.org_distribution',
                ),
              },
              {
                value: 'other',
                label: t(
                  'services::forms.shared_fields.advertiser.fields.advertiser_info.contract.subject_type.options.other',
                ),
              },
            ]}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t(
                'services::forms.shared_fields.advertiser.fields.advertiser_info.contract.subject_type.tooltip',
              )}
              placement="top"
              arrow
            >
              <Icon
                name={'information'}
                color={'Label-Secondary'}
                size={'m'}
              />
            </Tooltip>
          </div>
        </div>

        {isVk && advertiser_info?.contract?.subject_type === 'other' && (
          <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
            <Field
              fullWidth
              name="advertiser_info.contract.subject_type_description"
              component={renderInputField}
              label={t(
                'services::forms.shared_fields.advertiser.fields.advertiser_info.contract.subject_type_description.label',
              )}
              helperText={t(
                'services::forms.shared_fields.advertiser.fields.advertiser_info.contract.subject_type_description.helperText',
                {
                  length: advertiser_info?.contract.subject_type_description?.length || 0,
                },
              )}
              t={t}
              maxLength={255}
            />
          </div>
        )}

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            label={t(
              'services::forms.shared_fields.advertiser.fields.advertiser_info.contract.action_type.label',
            )}
            name="advertiser_info.contract.action_type"
            component={renderSelect}
            normalize={(value = '') => value}
            fullWidth
            t={t}
            emptyOptionLabel={t(
              'services::forms.shared_fields.advertiser.fields.advertiser_info.contract.action_type.options.empty',
            )}
            clearOptionLabel={t('common::select_clear_option')}
            options={[
              {
                value: 'commercial',
                label: t(
                  'services::forms.shared_fields.advertiser.fields.advertiser_info.contract.action_type.options.commercial',
                ),
              },
              {
                value: 'distribution',
                label: t(
                  'services::forms.shared_fields.advertiser.fields.advertiser_info.contract.action_type.options.distribution',
                ),
              },
              {
                value: 'conclude',
                label: t(
                  'services::forms.shared_fields.advertiser.fields.advertiser_info.contract.action_type.options.conclude',
                ),
              },
              {
                value: 'other',
                label: t(
                  'services::forms.shared_fields.advertiser.fields.advertiser_info.contract.action_type.options.other',
                ),
              },
            ]}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t(
                'services::forms.shared_fields.advertiser.fields.advertiser_info.contract.action_type.tooltip',
              )}
              placement="top"
              arrow
            >
              <Icon
                name={'information'}
                color={'Label-Secondary'}
                size={'m'}
              />
            </Tooltip>
          </div>
        </div>

        {isVk && advertiser_info?.contract?.action_type === 'other' && (
          <div className={clsx('margin-top-16', formStyles['row_with-tooltip'])}>
            <Field
              fullWidth
              name="advertiser_info.contract.action_type_description"
              component={renderInputField}
              label={t(
                'services::forms.shared_fields.advertiser.fields.advertiser_info.contract.action_type_description.label',
              )}
              helperText={t(
                'services::forms.shared_fields.advertiser.fields.advertiser_info.contract.action_type_description.helperText',
                {
                  length: advertiser_info?.contract.action_type_description?.length || 0,
                },
              )}
              t={t}
              maxLength={255}
            />
          </div>
        )}
        {actionButtons && <div className={'margin-top-40'}>{actionButtons}</div>}
      </div>
    </>
  )
}

AdvertiserFieldsExtended.propTypes = {
  builtInto: PropTypes.bool,
  form: PropTypes.string,
  networkType: PropTypes.string,
  showContractTypeOtherOption: PropTypes.bool,
  advertiser_info: PropTypes.object,
  actionButtons: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.element),
    PropTypes.node,
    PropTypes.oneOf([null]),
  ]),
}

export default AdvertiserFieldsExtended
